import { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import { useAddItemToBom, useItems, type Item } from '@/features/inventory/hooks';
import { UomSelect } from '@/components/inventory/UomSelect';
import { BomQtyPreview } from '@/components/manufacturing/BomQtyPreview';
import { toProducedQty } from '@/lib/uom';

/**
 * Which direction we're editing the BOM tree from:
 *
 *   * `add-to-parent` — the current item is the *component*; the
 *     operator picks a parent assembly to add it to. Launched from the
 *     "Used in (parent assemblies)" card.
 *   * `add-component` — the current item is the *assembly*; the
 *     operator picks a component to add to its own BOM. Launched from
 *     the "This item is built from" card.
 */
export type AddToBomMode = 'add-to-parent' | 'add-component';

interface AddToBomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current detail-page item. */
  item: Item;
  mode: AddToBomMode;
}

const COPY: Record<
  AddToBomMode,
  { title: string; pickerLabel: string; searchPlaceholder: string }
> = {
  'add-to-parent': {
    title: 'Add to a BOM',
    pickerLabel: 'Parent assembly',
    searchPlaceholder: 'Search assemblies by SKU or name…',
  },
  'add-component': {
    title: 'Add a component',
    pickerLabel: 'Component',
    searchPlaceholder: 'Search components by SKU or name…',
  },
};

/**
 * "Add to BOM" / "Add component" modal launched from the item's BOMs
 * tab. It edits a single `bom_lines` row either way — the only thing
 * that differs is which side of the relationship the current item sits
 * on, decided by `mode`. The `useAddItemToBom` hook owns the
 * find-or-create walk down boms → bom_versions → bom_lines, so a parent
 * that doesn't have a BOM yet gets one created on the fly.
 */
export function AddToBomDialog({ open, onOpenChange, item, mode }: AddToBomDialogProps) {
  const { data: items, isLoading: itemsLoading } = useItems();
  const addToBom = useAddItemToBom();
  const copy = COPY[mode];

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState(item.base_uom || 'each');

  // Candidate items: every other item in the catalog. We drop the
  // current item so it can't end up as a component of itself — the DB
  // has no such guard, so this is the only thing stopping a trivial
  // one-level cycle.
  const candidates = useMemo(
    () => (items ?? []).filter((it) => it.id !== item.id && it.status !== 'archived'),
    [items, item.id],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (it) => it.name.toLowerCase().includes(q) || it.sku.toLowerCase().includes(q),
    );
  }, [candidates, query]);

  const selected = useMemo(
    () => candidates.find((it) => it.id === selectedId) ?? null,
    [candidates, selectedId],
  );

  // Reset the draft on close so a previous selection doesn't bleed
  // through if the operator re-opens to edit another line.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedId(null);
      setQuantity('1');
      setUom(item.base_uom || 'each');
    }
  }, [open, item.base_uom]);

  // BOM lines count in the *component's* UoM Produced. In add-to-parent
  // mode the component is the current item (known up front); in
  // add-component mode it's whatever the operator selects, and the units
  // on offer depend on it, so re-seed the unit as the selection changes.
  const componentUom = mode === 'add-to-parent' ? item.base_uom : selected?.base_uom;
  useEffect(() => {
    if (mode !== 'add-component') return;
    setUom(selected?.base_uom || 'each');
  }, [mode, selected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      toast.error(
        mode === 'add-to-parent' ? 'Pick a parent assembly first.' : 'Pick a component first.',
      );
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }

    // In add-to-parent mode the selected item is the assembly; in
    // add-component mode it's the component. Map both onto the hook's
    // parent/component shape.
    const parent = mode === 'add-to-parent' ? selected : item;
    const component = mode === 'add-to-parent' ? item : selected;

    try {
      await addToBom.mutateAsync({
        parentItemId: parent.id,
        parentName: parent.name,
        componentItemId: component.id,
        ...toProducedQty(qty, uom, component.base_uom),
      });
      toast.success(
        mode === 'add-to-parent'
          ? `Added to ${selected.sku}’s BOM`
          : `Added ${selected.sku} to this BOM`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update BOM.');
    }
  };

  const submitting = addToBom.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>{copy.pickerLabel}</Label>
              <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                {itemsLoading ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {candidates.length === 0 ? 'No other items in the catalog yet.' : 'No matching items.'}
                  </div>
                ) : (
                  filtered.map((it) => {
                    const isSelected = it.id === selectedId;
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setSelectedId(it.id)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60',
                          isSelected && 'bg-secondary',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'opacity-40',
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">
                          {it.sku}
                        </span>
                        <span className="truncate">{it.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="bom-qty">Quantity per unit</Label>
                <Input
                  id="bom-qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputMode="decimal"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bom-uom">UoM</Label>
                <UomSelect
                  id="bom-uom"
                  value={uom}
                  onChange={setUom}
                  convertibleTo={componentUom}
                  disabled={!componentUom}
                />
              </div>
            </div>
            <BomQtyPreview quantity={quantity} uom={uom} producedUom={componentUom} />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !selected}>
              {submitting ? 'Adding…' : copy.title}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
